"use client";

import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { ReactNode } from "react";

interface Slide {
  title: ReactNode;
  description: string;
}

interface CarouselProps {
  slides: Slide[];
  autoPlayTime?: number;
}

const Carousel: React.FC<CarouselProps> = ({ slides, autoPlayTime = 3000 }) => {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const totalSlides = slides.length;

  // ✅ useCallback keeps the same function reference between renders
  const nextSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % totalSlides);
  }, [totalSlides]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const prevSlide = useCallback(() => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? totalSlides - 1 : prevIndex - 1
    );
  }, [totalSlides]);

  useEffect(() => {
    const autoPlay = setInterval(nextSlide, autoPlayTime);
    return () => clearInterval(autoPlay);
  }, [autoPlayTime, nextSlide]);

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "700px",
        margin: "auto",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Slides */}
      <Box
        sx={{
          display: "flex",
          transition: "transform 0.5s ease-in-out",
          transform: `translateX(-${currentIndex * 100}%)`,
        }}
      >
        {slides.map((slide: Slide, index: number) => (
          <Box
            key={index}
            sx={{
              flex: "0 0 100%",
              flexWrap: "wrap",
              padding: 4,
            }}
          >
            <div>{slide.title}</div>
            <Typography variant="body1" fontSize={18}>
              {slide.description}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default Carousel;
